import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

import CompanyAutocomplete
  from "../common/CompanyAutocomplete";

import {
  AdminLookup,
} from "../../pages/admin/section_engine";


export default function ActivityForm({
  value,
  onChange,
  blueprints = [],
  requireCompanyUuid = true,
  activityTypeLocked = false,
  companyLocked = false,
  accountCodes = [],
}) {
  const updateField = (
    event,
  ) => {
    const {
      name,
      value: fieldValue,
    } = event.target;

    onChange({
      ...value,
      [name]: fieldValue,
    });
  };


  const searchAccountCodes =
    (category) =>
      async (query) => {
        const normalizedQuery =
          String(query || "")
            .trim()
            .toLowerCase();

        return accountCodes
          .filter(
            (accountCode) =>
              accountCode.category
                === category
          )
          .filter(
            (accountCode) => {
              if (!normalizedQuery) {
                return true;
              }

              return [
                accountCode.code,
                accountCode.description,
                accountCode.subcategory,
              ]
                .filter(Boolean)
                .some(
                  (candidate) =>
                    String(candidate)
                      .toLowerCase()
                      .includes(
                        normalizedQuery
                      )
                );
            }
          )
          .map(
            (accountCode) => ({
              ...accountCode,

              id:
                accountCode.uuid
                || accountCode.code,

              label: [
                accountCode.code,
                accountCode.description,
              ]
                .filter(Boolean)
                .join(" — "),
            })
          );
      };


  const selectAccountCode =
    (fieldName) =>
      (accountCode) => {
        onChange({
          ...value,
          [fieldName]:
            accountCode?.code || "",
        });
      };


  const updateCompany = (
    companyData,
  ) => {
    if (companyLocked) {
      return;
    }

    onChange({
      ...value,
      ...companyData,
    });
  };


  return (
<FieldsGrid>
  <Field>
    <label htmlFor="activity-create-name">
      Nom activité
    </label>

    <input
      id="activity-create-name"
      name="name"
      value={value.name}
      onChange={updateField}
    />
  </Field>


  <Field>
    <label htmlFor="activity-create-type">
      Type
    </label>

    {activityTypeLocked ? (
      <input
        id="activity-create-type"
        name="activity_type"
        value={value.activity_type}
        readOnly
      />
    ) : (
      <select
        id="activity-create-type"
        name="activity_type"
        value={value.activity_type}
        onChange={updateField}
      >
        {blueprints.map(
          (blueprint) => (
            <option
              key={
                blueprint.key
                || blueprint.activity_type
              }
              value={
                blueprint.activity_type
              }
            >
              {blueprint.label}
            </option>
          ),
        )}
      </select>
    )}
  </Field>


  <CompanyAutocomplete
    value={value.company_name}
    siret={value.company_siret}
    onChange={updateCompany}
  />


  {requireCompanyUuid
  && value.company_name
  && !value.company_uuid ? (
    <CompanyRequirement
      role="status"
    >
      Sélectionnez la société dans
      les résultats proposés pour
      établir son rattachement.
    </CompanyRequirement>
  ) : null}


  <Field>
    <label htmlFor="activity-create-vat">
      TVA par défaut
    </label>

    <input
      id="activity-create-vat"
      type="number"
      step="0.01"
      name="default_vat_rate"
      value={value.default_vat_rate}
      onChange={updateField}
    />
  </Field>


  <Field>
    <label htmlFor="activity-create-sale-account">
      Compte vente
    </label>

    <AdminLookup
      value={
        value.sale_account_code
      }
      search={
        searchAccountCodes(
          "SALE"
        )
      }
      onSelect={
        selectAccountCode(
          "sale_account_code"
        )
      }
      ariaLabel="Compte vente"
      minChars={0}
      emptyLabel="Aucun compte de vente"
      renderOption={
        (accountCode) => (
          <div>
            <strong>
              {accountCode.code}
            </strong>

            {accountCode.description ? (
              <small>
                {" — "}
                {
                  accountCode.description
                }
              </small>
            ) : null}
          </div>
        )
      }
    />
  </Field>


  <Field>
    <label htmlFor="activity-create-purchase-account">
      Compte achat
    </label>

    <AdminLookup
      value={
        value.purchase_account_code
      }
      search={
        searchAccountCodes(
          "PURCHASE"
        )
      }
      onSelect={
        selectAccountCode(
          "purchase_account_code"
        )
      }
      ariaLabel="Compte achat"
      minChars={0}
      emptyLabel="Aucun compte d’achat"
      renderOption={
        (accountCode) => (
          <div>
            <strong>
              {accountCode.code}
            </strong>

            {accountCode.description ? (
              <small>
                {" — "}
                {accountCode.description}
              </small>
            ) : null}
          </div>
        )
      }
    />
  </Field>


  <Field>
    <label htmlFor="activity-create-vat-account">
      Compte TVA
    </label>

    <AdminLookup
      value={
        value.vat_account_code
      }
      search={
        searchAccountCodes(
          "TVA"
        )
      }
      onSelect={
        selectAccountCode(
          "vat_account_code"
        )
      }
      ariaLabel="Compte TVA"
      minChars={0}
      emptyLabel="Aucun compte TVA"
      renderOption={
        (accountCode) => (
          <div>
            <strong>
              {accountCode.code}
            </strong>

            {accountCode.description ? (
              <small>
                {" — "}
                {
                  accountCode.description
                }
              </small>
            ) : null}
          </div>
        )
      }
    />
  </Field>


  <Field>
    <label htmlFor="activity-create-revenue-label">
      Libellé comptable
    </label>

    <input
      id="activity-create-revenue-label"
      name="revenue_label"
      value={value.revenue_label}
      onChange={updateField}
    />
  </Field>


  <Field $full>
    <label htmlFor="activity-create-description">
      Description
    </label>

    <textarea
      id="activity-create-description"
      name="description"
      value={value.description}
      onChange={updateField}
    />
  </Field>
</FieldsGrid>
  );
}


ActivityForm.propTypes = {
  value:
    PropTypes.shape({
      name: PropTypes.string,
      activity_type: PropTypes.string,
      company_name: PropTypes.string,
      company_siret: PropTypes.string,
      company_uuid: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
      ]),
      description: PropTypes.string,
      default_vat_rate: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      sale_account_code: PropTypes.string,
      purchase_account_code: PropTypes.string,
      vat_account_code: PropTypes.string,
      revenue_label: PropTypes.string,
    }).isRequired,

  onChange:
    PropTypes.func.isRequired,

  blueprints:
    PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        activity_type: PropTypes.string,
        label: PropTypes.string,
      })
    ),

  requireCompanyUuid:
    PropTypes.bool,

  activityTypeLocked:
    PropTypes.bool,

  companyLocked:
    PropTypes.bool,

  accountCodes:
    PropTypes.arrayOf(
      PropTypes.shape({
        uuid:
          PropTypes.string,

        category:
          PropTypes.string,

        subcategory:
          PropTypes.string,

        code:
          PropTypes.string,

        description:
          PropTypes.string,
      })
    ),
};


const FieldsGrid = styled.div`
  display: grid;

  grid-template-columns:
    repeat(
      2,
      minmax(0, 1fr)
    );

  gap:
    1rem
    1.25rem;

  min-width: 0;
  width: 100%;

  @media (max-width: 720px) {
    grid-template-columns:
      minmax(0, 1fr);
  }
`;


const Field = styled.div`
  display: flex;
  flex-direction: column;

  gap: 0.45rem;

  min-width: 0;

  grid-column:
    ${({ $full }) =>
      $full
        ? "1 / -1"
        : "auto"};

  label {
    color: #f7f7f7;

    font-size: 0.86rem;
    font-weight: 700;
  }

  input,
  select,
  textarea {
    width: 100%;
    min-width: 0;

    box-sizing: border-box;

    padding:
      0.7rem
      0.8rem;

    border:
      1px solid
      rgba(
        255,
        255,
        255,
        0.12
      );

    border-radius: 10px;

    background:
      rgba(
        7,
        15,
        35,
        0.42
      );

    color: #f7f7f7;
    font: inherit;

    outline: none;

    transition:
      border-color 0.16s ease,
      background 0.16s ease;
  }

  input:focus,
  select:focus,
  textarea:focus {
    border-color:
      rgba(
        96,
        165,
        250,
        0.8
      );

    background:
      rgba(
        7,
        15,
        35,
        0.62
      );
  }

  input:read-only {
    opacity: 0.72;
    cursor: default;
  }

  textarea {
    min-height: 7rem;
    resize: vertical;
  }
`;


const CompanyRequirement = styled.div`
  grid-column: 1 / -1;
  font-size: 0.82rem;
  opacity: 0.82;
`;
